<template>
    <div class="report__item__wrap">
        <div>
            <div class="flex align-items-start">
                <div class="flex-2 grid gap-3 mr--10">
                    <CustomLineChart :status-data="transformedData.clientsByMonth">
                        <template #header-caption>
                            <div class="mb--25">
                                <FormLabel title="Monthly Distribution" label-type="title" class="mb--0" />
                                <p class="text--xs text-gray-600">
                                    Showing distribution of clients created on a monthly basis
                                </p>
                            </div>
                        </template>
                    </CustomLineChart>

                    <PieChartReport field="client" :stats-data="transformedData.clientsByIndustry">
                        <template #header-caption>
                            <div class="mb--25">
                                <FormLabel title="Industry Distribution" label-type="title" class="mb--0" />
                                <p class="text--xs text-gray-600">
                                    Showing distribution of clients by industry
                                </p>
                            </div>
                        </template>
                    </PieChartReport>
    
                    <PieChartReport field="client" :stats-data="transformedData.clientsByCountry">
                        <template #header-caption>
                            <div class="mb--15">
                                <FormLabel title="Geographical Distribution" label-type="title" class="mb--0" />
                                <p class="text--xs text-gray-600">
                                    Showing distribution of clients by location/country 
                                </p>
                            </div>
                        </template>
                    </PieChartReport>
                </div>

                <div class="flex-1 grid gap-3">
                    <StatusDistribution  
                        :status-data="statusData"
                        label="Status Count"
                        field="client"
                    >
                        <template #header-caption>
                            <div class="mb--25">
                                <FormLabel title="Status Count" label-type="title" class="mb--0" />
                                <p class="text--xs text-gray-600">
                                    Showing client count grouped by status
                                </p>
                            </div>
                        </template>
                    </StatusDistribution>

                    <StatusPercentage  
                        :status-data="ratesData"
                        label="Percentage Status"
                    >
                        <template #header-caption>
                            <div class="mb--25">
                                <FormLabel title="Percentage Status" label-type="title" class="mb--0" />
                                <p class="text--xs text-gray-600">
                                    Showing client percentage grouped by status
                                </p>
                            </div>
                        </template>
                    </StatusPercentage>

                    <div>
                        <div class="mb--15">
                            <FormLabel title="Recent Clients" label-type="title" class="mb--0" />
                            <p class="text--xs text-gray-600">
                                Showing your most recently created clients
                            </p>
                        </div>

                        <div>
                            <template v-if="mostRecentState === 'loading'">
                                <PageSpinner />
                            </template>

                            <template v-else-if="mostRecentState === 'error'">
                                <EmptyPage state="error" @action="handleFetchMostRecent" />
                            </template>

                            <template v-else>
                                <div class="grid gap-2">
                                    <template v-if="recentClients.length">
                                        <BaseCard v-for="item in recentClients" :key="item._id" shadow>
                                            <template #card-body>
                                                <div class="outer__wrap">
                                                    <div class="flex align-items-center w-100 justify-content-between">
                                                        <div class="flex align-items-center w-100 mt--3">
                                                            <IconWrap icon-name="user" style="width: 30px; height: 30px;" />
                                                            <div class="ml--15 flex-2">
                                                                <p class="text--capitalize text--sm text--medium text-gray-600 mb--5">
                                                                    {{ item.name }}
                                                                </p>
                                                                <div>
                                                                    <Status type="client" :value="item.status" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div class="cursor-pointer" @click="goToClientsPage(item._id)">
                                                            <IconSvg name="right-arrow-alt" fill="#666" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                        </BaseCard>
                                    </template>
                                    <template v-else>
                                        <EmptyPage 
                                            state="empty_custom" 
                                            icon-name="report" 
                                            type="data"
                                            title=" No data available." 
                                        />
                                    </template>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getMonthFromNum } from "@/utils/date";
import { clientFilterStatuses }from "@/utils/enums";
import StatusDistribution from "../stats/StatusDistribution.vue";
import StatusPercentage from "../stats/StatusPercentage.vue";
import PieChartReport from "../stats/PieChartReport.vue";
import CustomLineChart from "../stats/CustomLineChart.vue";
import { Status, IconWrap, PageSpinner, EmptyPage, BaseCard, IconSvg, FormLabel } from "../ui";

export default {
    name: "ClientReports",

    components: {
        StatusDistribution,
        StatusPercentage,
        PieChartReport,
        FormLabel,
        CustomLineChart,
        IconSvg,
        BaseCard,
        EmptyPage,
        PageSpinner,
        Status,
        IconWrap,
    },

    data() {
        return {
            clientFilterStatuses,
            mostRecentState: "",
        };
    },

    computed: {
        reportDataProps() {
            return this.$store.getters["report/reportData"].clients;
        },

        transformedData() {
            if (!this.reportDataProps) return {};
            
            const data = {};
            const keys = Object.keys(this.reportDataProps);

            if (!keys && keys.length) return {};

            for (let i = 0; i < keys.length; i++) {
                const item = keys[i];
                const list = this.reportDataProps[item]; // an array

                switch(item) {
                case "clientsByCountry":                    
                    data.clientsByCountry = list.map(x => ([x._id, x.count])) || [];
                    break;
                        
                case "clientsByMonth":
                    data.clientsByMonth = list.map(i => ([this.getMonthFromNum(i?._id), i?.count])) || [];
                    break;

                case "clientsByStatus":
                    data.clientsByStatus = list || [];
                    break;

                case "clientsByIndustry":                    
                    data.clientsByIndustry = list.map(x => ([x._id, x.count])) || [];
                    break;

                case "totalNum":
                    data.totalNum = list.length && list[0]?.count || 0;
                    break;

                case "averageCreatedClient":
                    data.avgClients = list.length && list[0]?.avgClients || 0;
                    break;
               
                case "averageOnboardedClient":
                    data.avgActiveClients = list.length && list[0]?.avgActiveClients || 0;
                    break;

                default:
                    break;
                }
            }

            return data;
        },

        ratesData() {
            const statusMap = new Map(this.transformedData.clientsByStatus?.map(item => item && [item._id, item.count]));
            const totalNum = this.transformedData.totalNum;
            
            return clientFilterStatuses.map(item => {
                const count = statusMap.get(item) || 0;
                const percentageValue = totalNum > 0 ? ((count / totalNum) * 100).toFixed(0) : "0";
                
                return {
                    id: item,
                    percentageValue,
                };
            });
        },

        statusData() {
            const statusMap = new Map(this.transformedData.clientsByStatus?.map(item => [item._id, item.count]));
            return clientFilterStatuses.map(item => {
                const count = statusMap.get(item) || 0;
                
                return {
                    id: item,
                    count ,
                };
            });
        },

        totalNum() {
            return this.transformedData?.totalNum;
        },

        recentClients() {
            return this.$store.getters["report/recentClients"];
        },
    },

    async mounted() {
        await this.handleFetchMostRecent();
    },

    methods: {
        getMonthFromNum, 
    
        async handleFetchMostRecent() {
            try {                
                this.mostRecentState = "loading";
                await this.$store.dispatch("report/fetchMostRecentClients", { refresh: true });
                this.mostRecentState = "";
            } catch (error) {
                this.mostRecentState = "error";
            }
        },

        goToClientsPage(id) {
            if (!id) return;
            this.$router.push(`/dashboard/clients/${id}`);
        },
    },
};
</script>

<style lang="scss" scoped>
    .report__bottom {
        display: flex;
    }
</style>